import type { AccessControlProvider } from '@refinedev/core';
import type { AppRole } from '@lebinatikra/shared-types';
import { supabaseClient } from './supabase-client';

export type AccessAction = 'list' | 'show' | 'create' | 'edit' | 'delete';
export type AccessResource =
  | 'communities'
  | 'organizations'
  | 'users'
  | 'groups'
  | 'audit-log'
  | 'moderation-queue'
  | 'community-dashboard'
  | 'organization-dashboard';

// Matrix in Dev Notes §3 of story 3.6. UI-only shim — RLS is sole source of truth.
// can: true → show button; backend still independently authorizes.
export function canRole(role: AppRole, resource: AccessResource, action: AccessAction): boolean {
  if (resource === 'audit-log') {
    if (action !== 'list' && action !== 'show') return false;
    return role === 'super_admin' || role === 'community_admin' || role === 'org_admin' || role === 'moderator';
  }

  if (resource === 'moderation-queue') {
    if (action !== 'list' && action !== 'create') return false;
    return role === 'super_admin' || role === 'community_admin' || role === 'org_admin' || role === 'moderator';
  }

  // Story 3.7d — dashboard resources mirror backend AC2/AC3 authz:
  //   community-dashboard.list = super_admin + community_admin (own community)
  //   organization-dashboard.list = super_admin + community_admin + org_admin
  // The UI shim does not see the URL params, so we allow on role; the backend
  // (and the RLS layer) is the source of truth for cross-tenant rejection.
  if (resource === 'community-dashboard') {
    if (action !== 'list') return false;
    return role === 'super_admin' || role === 'community_admin';
  }
  if (resource === 'organization-dashboard') {
    if (action !== 'list') return false;
    return role === 'super_admin' || role === 'community_admin' || role === 'org_admin';
  }

  if (role === 'super_admin') return true;

  if (role === 'community_admin') {
    if (resource === 'communities') {
      return action === 'list' || action === 'show' || action === 'edit';
    }
    if (resource === 'organizations' || resource === 'groups' || resource === 'users') {
      return true;
    }
    return false;
  }

  if (role === 'org_admin') {
    if (resource === 'organizations') {
      return action === 'list' || action === 'show' || action === 'edit';
    }
    if (resource === 'groups') return true;
    if (resource === 'users') {
      return action === 'list' || action === 'show' || action === 'create' || action === 'edit';
    }
    if (resource === 'communities') return action === 'list' || action === 'show';
    return false;
  }

  // member / teacher / moderator / student / bachur / parent / avrech — read-only
  return action === 'list' || action === 'show';
}

export const accessControlProvider: AccessControlProvider = {
  can: async ({ action, resource }) => {
    if (!resource || !action) return { can: true };
    const { data } = await supabaseClient.auth.getUser();
    const meta = (data.user?.app_metadata ?? {}) as Record<string, unknown>;
    const role = ((meta.role as string | undefined) ?? 'member') as AppRole;
    const allowed = canRole(role, resource as AccessResource, action as AccessAction);
    return allowed ? { can: true } : { can: false, reason: 'FORBIDDEN_INSUFFICIENT_ROLE' };
  },
};
